import { useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import useAuth from '@/hooks/auth';
import userActivityTracker from '@/services/userActivityTracker';

/**
 * Custom hook for user activity tracking
 * Provides easy-to-use methods for tracking various user activities
 */
const useActivityTracker = () => {
  const router = useRouter();
  const { user } = useAuth();

  // Get user IP from localStorage (set in _app.js)
  const getUserIP = useCallback(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lctd-ia');
    }
    return null;
  }, []);

  /**
   * Track page views automatically
   */
  useEffect(() => {
    const handleRouteChange = (url) => {
      // Small delay to ensure page title is updated
      setTimeout(() => {
        userActivityTracker.trackPageView({
          url: `${process.env.NEXT_PUBLIC_BASE_URL || ''}${url}`,
          title: document.title,
          user: user,
          ipAddress: getUserIP()
        });
      }, 100);
    };

    // Track initial page load
    if (typeof window !== 'undefined') {
      handleRouteChange(router.asPath);
    }

    // Track route changes
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, router.asPath, user, getUserIP]);

  /**
   * Track content interactions (reports, articles, startups, VCs, etc.)
   */
  const trackContentView = useCallback((contentType, contentId, contentTitle) => {
    userActivityTracker.trackContentInteraction({
      contentType,
      contentId,
      contentTitle,
      action: 'view',
      user,
      ipAddress: getUserIP()
    });
  }, [user, getUserIP]);

  /**
   * Track content downloads
   */
  const trackContentDownload = useCallback((contentType, contentId, contentTitle) => {
    userActivityTracker.trackContentInteraction({
      contentType,
      contentId,
      contentTitle,
      action: 'download',
      user,
      ipAddress: getUserIP()
    });
  }, [user, getUserIP]);

  /**
   * Track content sharing
   */
  const trackContentShare = useCallback((contentType, contentId, contentTitle, platform) => {
    userActivityTracker.trackContentInteraction({
      contentType,
      contentId,
      contentTitle,
      action: 'share',
      user,
      ipAddress: getUserIP(),
      additionalData: {
        resource_type: `${contentType}_share`,
        platform
      }
    });
  }, [user, getUserIP]);

  /**
   * Track button clicks
   */
  const trackButtonClick = useCallback((buttonName, section = '', additionalData = {}) => {
    userActivityTracker.trackInteraction({
      element: 'button',
      action: 'click',
      label: `User clicked ${buttonName}${section ? ` in ${section}` : ''}`,
      user,
      ipAddress: getUserIP(),
      additionalData: {
        resource_type: 'button_click',
        button_name: buttonName,
        section,
        ...additionalData
      }
    });
  }, [user, getUserIP]);

  /**
   * Track form submissions
   */
  const trackFormSubmission = useCallback((formName, formData = {}) => {
    userActivityTracker.trackInteraction({
      element: 'form',
      action: 'submit',
      label: `User submitted ${formName} form`,
      user,
      ipAddress: getUserIP(),
      additionalData: {
        resource_type: 'form_submission',
        form_name: formName,
        ...formData
      }
    });
  }, [user, getUserIP]);

  /**
   * Track search activities
   */
  const trackSearch = useCallback((query, section = '', results = 0) => {
    userActivityTracker.trackInteraction({
      element: 'search',
      action: 'search',
      label: `User searched for "${query}"${section ? ` in ${section}` : ''}`,
      user,
      ipAddress: getUserIP(),
      additionalData: {
        resource_type: 'search',
        query,
        section,
        results_count: results
      }
    });
  }, [user, getUserIP]);

  /**
   * Track filter usage
   */
  const trackFilter = useCallback((filterType, filterValue, section = '') => {
    userActivityTracker.trackInteraction({
      element: 'filter',
      action: 'filter',
      label: `User applied ${filterType} filter: ${filterValue}${section ? ` in ${section}` : ''}`,
      user,
      ipAddress: getUserIP(),
      additionalData: {
        resource_type: 'filter_usage',
        filter_type: filterType,
        filter_value: filterValue,
        section
      }
    });
  }, [user, getUserIP]);

  /**
   * Track modal interactions
   */
  const trackModal = useCallback((modalName, action) => {
    userActivityTracker.trackInteraction({
      element: 'modal',
      action: `modal_${action}`,
      label: `User ${action} ${modalName} modal`,
      user,
      ipAddress: getUserIP(),
      additionalData: {
        resource_type: 'modal_interaction',
        modal_name: modalName,
        action
      }
    });
  }, [user, getUserIP]);

  /**
   * Track video interactions
   */
  const trackVideo = useCallback((videoId, videoTitle, action, currentTime = 0) => {
    userActivityTracker.trackInteraction({
      element: 'video',
      action: `video_${action}`,
      label: `User ${action} video: ${videoTitle}`,
      user,
      ipAddress: getUserIP(),
      additionalData: {
        resource_type: 'video',
        resource_type_id: videoId,
        video_title: videoTitle,
        action,
        current_time: currentTime
      }
    });
  }, [user, getUserIP]);

  /**
   * Track custom events
   */
  const trackCustomEvent = useCallback((eventName, eventData = {}) => {
    userActivityTracker.track({
      full_uri: typeof window !== 'undefined' ? window.location.href : '',
      page_title: typeof document !== 'undefined' ? document.title : '',
      event_name: eventName,
      label: eventData.label || `Custom event: ${eventName}`,
      email: user?.email || null,
      ip_address: getUserIP(),
      ...eventData
    });
  }, [user, getUserIP]);

  return {
    // Content tracking
    trackContentView,
    trackContentDownload,
    trackContentShare,
    
    // Interaction tracking
    trackButtonClick,
    trackFormSubmission,
    trackSearch,
    trackFilter,
    trackModal,
    trackVideo,
    
    // Custom tracking
    trackCustomEvent,
    
    // Utility
    isEnabled: userActivityTracker.isEnabled,
    queueSize: userActivityTracker.getQueueSize(),
  };
};

export default useActivityTracker;